/**
 * Plugin that shows a placeholder when the document is empty, using a decoration widget.
 */

import type { EditorState } from "prosemirror-state";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import type { PlaceholderPluginOptions } from "../types";

function isEmptyDoc(state: EditorState): boolean {
  const { doc } = state;
  // Doc always has 1 or more children (schema: block+); we only show the placeholder when there is exactly one empty paragraph
  if (doc.childCount !== 1) return false;
  const first = doc.firstChild;
  return first !== null && first.type.name === "paragraph" && first.content.size === 0;
}

/**
 * Placeholder plugin
 *
 * @param options - When and what placeholder to show (showPlaceholder, placeHolderText, readOnly)
 * @returns A ProseMirror plugin
 */
export function placeholderPlugin(options: PlaceholderPluginOptions): Plugin {
  const { showPlaceholder, placeHolderText, readOnly } = options;

  return new Plugin({
    props: {
      decorations(state) {
        if (!showPlaceholder || readOnly || !placeHolderText || !isEmptyDoc(state)) {
          return DecorationSet.empty;
        }

        const widget = Decoration.widget(
          1,
          () => {
            const span = document.createElement("span");
            span.className =
              "prosemirror-placeholder absolute left-0 top-0 pointer-events-none select-none text-black-tint-55";
            span.textContent = placeHolderText;
            return span;
          },
          { side: -1 },
        );

        return DecorationSet.create(state.doc, [widget]);
      },
    },
  });
}
